/**
 * <scrapbook-book>
 *
 * An open two-page book whose pages turn with a 3D hinge flip. Pages are the
 * .scrapbook-page children of [data-pages], in reading order: page 1 is the left page
 * of the first spread, page 2 the right page, page 3 the left page of the
 * second spread, and so on. On desktop the book shows a spread (two pages);
 * under 750px it shows one page at a time.
 *
 * A desktop flip is two quarter-turns on two different elements about the same
 * spine line: the outgoing page rotates to 90° (edge-on), then the incoming
 * page rotates in from 90°. Nothing is re-parented and no preserve-3d is
 * needed, so the theme editor's block outlines and Safari both behave.
 *
 * On phones the book is held like a paperback: every page hinges on its left
 * edge and the next page already sits underneath. Turning forward swings the
 * current page through 180° over the hinge (its back is hidden, so it vanishes
 * edge-on and reveals the page below); turning back swings the previous page
 * in the same way in reverse. Resting state is expressed with classes
 * (is-shown / is-under / is-flipping); the CSS in sections/scrapbook.liquid
 * does the rest.
 *
 * Markup contract (rendered by sections/scrapbook.liquid):
 *   [data-pages]     -> container whose .scrapbook-page children are the pages, in
 *                       order (other children — dots, arrows, other blocks — are ignored)
 *   [data-mobile-swap] on a page -> on phones, that page and its spread partner
 *                       show in the opposite order
 *   [data-dots-nav]  -> where the dots are rendered (one per spread / page)
 *   [data-prev] / [data-next] -> optional arrow buttons; disabled at either end
 *   data-autoplay    -> presence enables autoplay
 *   data-interval    -> autoplay interval in ms
 *   data-flip-ms     -> total flip duration in ms
 *   data-dots        -> presence renders the dots
 */
const MOBILE = '(max-width: 749px)';
const EASE_IN = 'cubic-bezier(0.55, 0, 1, 0.45)';
const EASE_OUT = 'cubic-bezier(0, 0.55, 0.45, 1)';

class ScrapbookBook extends HTMLElement {
  #pages = [];
  /** Pages in phone reading order: spreads whose page carries data-mobile-swap are reversed. */
  #mobile = [];
  #index = 0;
  #mode = 'spread';
  #flipping = null;
  #timer = null;
  #paused = false;
  #suspended = false;
  #visible = false;
  #preloaded = false;
  #mq = null;
  #io = null;
  #mo = null;
  #reducedMotion = false;
  #swipe = null;

  connectedCallback() {
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#mq = window.matchMedia(MOBILE);
    this.#collect();
    this.#mode = this.#mq.matches ? 'single' : 'spread';
    this.#index = this.#clamp(parseInt(this.getAttribute('data-start'), 10) || 0);
    this.#apply(this.#index);
    this.setAttribute('data-ready', '');

    this.#mq.addEventListener('change', this.#onBreakpoint);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.#bindSwipe();

    this.#io = new IntersectionObserver(this.#onIntersect, { threshold: 0.1, rootMargin: '200px 0px' });
    this.#io.observe(this);

    const pages = this.querySelector('[data-pages]');
    if (pages) {
      this.#mo = new MutationObserver(this.#onMutation);
      this.#mo.observe(pages, { childList: true });
    }
  }

  disconnectedCallback() {
    this.#stop();
    this.#io?.disconnect();
    this.#mo?.disconnect();
    this.#mq?.removeEventListener('change', this.#onBreakpoint);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  /* ---------------------------------------------------------------- public */

  get index() {
    return this.#index;
  }

  get count() {
    return this.#mode === 'spread' ? Math.ceil(this.#pages.length / 2) : this.#pages.length;
  }

  get mode() {
    return this.#mode;
  }

  get flipping() {
    return this.#flipping !== null;
  }

  next() {
    return this.goTo(this.#index + 1);
  }

  prev() {
    return this.goTo(this.#index - 1);
  }

  /**
   * Turn to a spread (desktop) or page (mobile).
   * @param {number} target
   * @param {{ instant?: boolean }} [options]
   */
  async goTo(target, { instant = false } = {}) {
    target = this.#clamp(target);
    if (this.#flipping) {
      const inflight = this.#flipping.target;
      this.#cancelFlip();
      this.#apply(inflight);
    }
    if (target === this.#index) return;

    if (instant || this.#reducedMotion || typeof Element.prototype.animate !== 'function') {
      this.#crossfade(target);
      return;
    }
    await this.#flip(this.#index, target);
  }

  /* --------------------------------------------------------------- state */

  #collect() {
    const pages = this.querySelector('[data-pages]');
    // Only page blocks are pages; the dots, the arrows and any other block share the container.
    this.#pages = pages ? Array.from(pages.children).filter((el) => el.classList.contains('scrapbook-page')) : [];
    this.#mobile = [];
    for (let i = 0; i < this.#pages.length; i += 2) {
      const pair = this.#pages.slice(i, i + 2);
      if (pair.some((p) => p.hasAttribute('data-mobile-swap'))) pair.reverse();
      this.#mobile.push(...pair);
    }
  }

  #clamp(i) {
    return Math.max(0, Math.min(this.count - 1, i || 0));
  }

  #pagesOf(i) {
    if (this.#mode === 'spread') return [this.#pages[2 * i], this.#pages[2 * i + 1]].filter(Boolean);
    return [this.#mobile[i]].filter(Boolean);
  }

  /** Resting state: no animation, classes only. */
  #apply(i) {
    this.#index = this.#clamp(i);
    const shown = new Set(this.#pagesOf(this.#index));
    for (const page of this.#pages) {
      const on = shown.has(page);
      page.classList.toggle('is-shown', on);
      page.classList.remove('is-under', 'is-flipping');
      page.style.transform = '';
      page.style.opacity = '';
      page.toggleAttribute('inert', !on);
      page.setAttribute('aria-hidden', on ? 'false' : 'true');
      const shade = page.querySelector(':scope > .scrapbook-page__shade');
      if (shade) shade.style.opacity = '';
    }
    // Page-stack depth on each outer edge (desktop CSS draws up to three edges).
    this.dataset.stackLeft = String(Math.min(3, this.#index));
    this.dataset.stackRight = String(Math.min(3, Math.max(0, this.count - 1 - this.#index)));
    this.#renderDots();
    this.#renderArrows();
    this.dispatchEvent(new CustomEvent('scrapbook:change', { detail: { index: this.#index, mode: this.#mode } }));
  }

  #renderDots() {
    const nav = this.querySelector('[data-dots-nav]');
    if (!nav) return;
    if (!this.hasAttribute('data-dots') || this.count < 2) {
      nav.replaceChildren();
      return;
    }
    if (nav.children.length !== this.count) {
      const dots = [];
      for (let i = 0; i < this.count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'scrapbook__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `${this.#mode === 'spread' ? 'Spread' : 'Page'} ${i + 1} of ${this.count}`);
        dot.dataset.index = String(i);
        dots.push(dot);
      }
      nav.replaceChildren(...dots);
    }
    Array.from(nav.children).forEach((dot, i) => {
      const active = i === this.#index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
      dot.tabIndex = active ? 0 : -1;
    });
  }

  /** Arrows stop at either end, like the dots; only autoplay wraps. */
  #renderArrows() {
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.toggleAttribute('disabled', this.#index <= 0);
    if (next) next.toggleAttribute('disabled', this.#index >= this.count - 1);
  }

  /* ---------------------------------------------------------------- flip */

  #flipMs() {
    return parseInt(this.getAttribute('data-flip-ms'), 10) || 900;
  }

  /** Which elements move for a flip from → to. */
  #actors(from, to, forward) {
    if (this.#mode === 'single') {
      return { out: this.#mobile[from], inn: this.#mobile[to], under: null };
    }
    const [fromL, fromR] = [this.#pages[2 * from], this.#pages[2 * from + 1]];
    const [toL, toR] = [this.#pages[2 * to], this.#pages[2 * to + 1]];
    return forward ? { out: fromR, inn: toL, under: toR } : { out: fromL, inn: toR, under: toL };
  }

  async #flip(from, to) {
    if (this.#mode === 'single') {
      await this.#flipSingle(from, to);
      return;
    }
    const forward = to > from;
    const half = this.#flipMs() / 2;
    const { out, inn, under } = this.#actors(from, to, forward);
    // Forward: the right page swings left (toward the viewer, negative), the
    // left page of the next spread lands from +90. Backward is the mirror.
    const outTo = forward ? -90 : 90;
    const inFrom = forward ? 90 : -90;

    if (!out || !inn) {
      this.#crossfade(to);
      return;
    }

    this.#preload();
    under?.classList.add('is-under');
    this.#setDotsDisabled(true);

    // The page the lifted one faces stays put for the whole turn: the lifted
    // page's shadow sweeps across it, then the incoming page lands on top of it.
    const facing = this.#pagesOf(from).find((p) => p !== out && !this.#pagesOf(to).includes(p)) || null;
    const facingShade = facing?.querySelector(':scope > .scrapbook-page__shade') || null;

    out.classList.add('is-flipping');
    const anims = [
      out.animate([{ transform: 'rotateY(0deg)' }, { transform: `rotateY(${outTo}deg)` }], {
        duration: half,
        easing: EASE_IN,
        fill: 'forwards',
      }),
    ];
    const outShade = out.querySelector(':scope > .scrapbook-page__shade');
    if (outShade) anims.push(outShade.animate([{ opacity: 0 }, { opacity: 1 }], { duration: half, fill: 'forwards' }));
    // Shadow creeps out from the fold over the facing page as the lifted page rises.
    const sweepIn = facingShade
      ? facingShade.animate([{ opacity: 0 }, { opacity: 0.65 }], { duration: half, easing: EASE_IN, fill: 'forwards' })
      : null;
    if (sweepIn) anims.push(sweepIn);
    this.#flipping = { anims, target: to };

    await anims[0].finished.catch(() => {});
    if (this.#flipping?.target !== to || this.#flipping.anims !== anims) return;
    // Keep the facing page's sweep where it is; phase two continues it.
    anims.forEach((a) => a !== sweepIn && a.cancel());
    out.classList.remove('is-flipping');
    out.classList.remove('is-shown');
    // The facing page drops beneath the incoming one instead of vanishing.
    if (facing) facing.classList.replace('is-shown', 'is-under');

    inn.classList.add('is-flipping');
    const anims2 = [
      inn.animate([{ transform: `rotateY(${inFrom}deg)` }, { transform: 'rotateY(0deg)' }], {
        duration: half,
        easing: EASE_OUT,
        fill: 'forwards',
      }),
    ];
    const inShade = inn.querySelector(':scope > .scrapbook-page__shade');
    if (inShade) anims2.push(inShade.animate([{ opacity: 1 }, { opacity: 0 }], { duration: half, fill: 'forwards' }));
    // The shadow clears off the facing page as the new page settles over it.
    if (facingShade) {
      anims2.push(facingShade.animate([{ opacity: 0.65 }, { opacity: 0 }], { duration: half, easing: EASE_OUT, fill: 'forwards' }));
      if (sweepIn) anims2.push(sweepIn);
    }
    this.#flipping = { anims: anims2, target: to };

    await anims2[0].finished.catch(() => {});
    if (this.#flipping?.target !== to || this.#flipping.anims !== anims2) return;
    anims2.forEach((a) => a.cancel());
    this.#flipping = null;
    this.#setDotsDisabled(false);
    this.#apply(to);
    this.#restart();
  }

  /**
   * Phone turn: one page swings over the left hinge while the page it reveals
   * (forward) or covers (backward) stays put underneath. The turning page's
   * back face is hidden by CSS, so it disappears as it passes edge-on; the
   * sweep stops just past that point rather than playing an invisible half.
   */
  async #flipSingle(from, to) {
    const forward = to > from;
    const cur = this.#mobile[from];
    const nxt = this.#mobile[to];
    if (!cur || !nxt) {
      this.#crossfade(to);
      return;
    }
    // Forward accelerates like a page let go; backward arrives fast and settles.
    const duration = Math.round(this.#flipMs() * 0.75);
    const easing = forward ? EASE_IN : EASE_OUT;
    this.#preload();
    this.#setDotsDisabled(true);

    // Forward: the current page lifts and the next one is already beneath it.
    // Backward: the current page stays and the previous one folds back over it.
    const mover = forward ? cur : nxt;
    if (forward) nxt.classList.add('is-under');
    mover.classList.add('is-flipping');

    const frames = forward
      ? [{ transform: 'rotateY(0deg)' }, { transform: 'rotateY(-110deg)' }]
      : [{ transform: 'rotateY(-110deg)' }, { transform: 'rotateY(0deg)' }];
    const anims = [mover.animate(frames, { duration, easing, fill: 'forwards' })];
    const shade = mover.querySelector(':scope > .scrapbook-page__shade');
    if (shade) {
      // Darken from the hinge as the page lifts; clear as it lies flat.
      const shadeFrames = forward ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }];
      anims.push(shade.animate(shadeFrames, { duration, easing, fill: 'forwards' }));
    }
    this.#flipping = { anims, target: to };

    await anims[0].finished.catch(() => {});
    if (this.#flipping?.target !== to || this.#flipping.anims !== anims) return;
    anims.forEach((a) => a.cancel());
    this.#flipping = null;
    this.#setDotsDisabled(false);
    this.#apply(to);
    this.#restart();
  }

  #cancelFlip() {
    if (!this.#flipping) return;
    this.#flipping.anims.forEach((a) => a.cancel());
    this.#flipping = null;
    this.#setDotsDisabled(false);
  }

  #crossfade(to) {
    const incoming = this.#pagesOf(to);
    if (this.#reducedMotion || typeof Element.prototype.animate !== 'function') {
      this.#apply(to);
      this.#restart();
      return;
    }
    this.#preload();
    incoming.forEach((p) => {
      p.classList.add('is-flipping');
      p.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 250, easing: 'ease-out' });
    });
    this.#apply(to);
    this.#restart();
  }

  #setDotsDisabled(disabled) {
    this.querySelectorAll('[data-dots-nav] .scrapbook__dot').forEach((d) => {
      d.toggleAttribute('disabled', disabled);
    });
  }

  #preload() {
    if (this.#preloaded) return;
    this.#preloaded = true;
    this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  /* ------------------------------------------------------------- autoplay */

  #interval() {
    return parseInt(this.getAttribute('data-interval'), 10) || 7000;
  }

  #start() {
    if (this.#timer) return;
    if (!this.hasAttribute('data-autoplay')) return;
    if (this.#reducedMotion || this.#paused || this.#suspended || !this.#visible || document.hidden) return;
    if (this.count < 2) return;
    this.#timer = window.setInterval(() => {
      if (this.#flipping) return;
      const next = (this.#index + 1) % this.count;
      this.goTo(next);
    }, this.#interval());
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #restart() {
    this.#stop();
    this.#start();
  }

  #pause = () => {
    this.#paused = true;
    this.#stop();
  };

  #resume = () => {
    this.#paused = false;
    this.#start();
  };

  #onVisibility = () => {
    document.hidden ? this.#stop() : this.#start();
  };

  #onIntersect = ([entry]) => {
    this.#visible = entry.isIntersecting;
    if (entry.isIntersecting) {
      this.#preload();
      this.#start();
    } else {
      this.#stop();
    }
  };

  /* --------------------------------------------------------------- input */

  #onKeydown = (event) => {
    if (event.defaultPrevented) return;
    const target = event.target;
    if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable]')) return;
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.prev();
        break;
      case 'Home':
        event.preventDefault();
        this.goTo(0);
        break;
      case 'End':
        event.preventDefault();
        this.goTo(this.count - 1);
        break;
      default:
        return;
    }
    this.#restart();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const dot = target.closest('[data-dots-nav] .scrapbook__dot');
    if (dot) {
      this.goTo(parseInt(dot.dataset.index, 10) || 0);
      this.#restart();
      return;
    }
    const arrow = target.closest('[data-prev], [data-next]');
    if (arrow && this.contains(arrow)) {
      arrow.hasAttribute('data-next') ? this.next() : this.prev();
      this.#restart();
      return;
    }
    // Tapping a page turns it, unless the tap was on something interactive or
    // we're in the theme editor (where a click selects the block).
    if (window.Shopify?.designMode) return;
    if (target.closest('a, button, input, select, textarea, label, video, [data-no-flip]')) return;
    const page = target.closest('.scrapbook-page');
    if (!page || !this.contains(page)) return;
    if (this.#swipe?.moved) return;
    const rect = page.getBoundingClientRect();
    const side = this.#mode === 'spread' ? this.#pages.indexOf(page) % 2 : event.clientX - rect.left > rect.width / 2 ? 1 : 0;
    side === 1 ? this.next() : this.prev();
    this.#restart();
  };

  #bindSwipe() {
    const pages = this.querySelector('[data-pages]');
    if (!pages) return;
    pages.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (!(e.target instanceof Element) || !e.target.closest('.scrapbook-page')) return;
      this.#swipe = { x: e.clientX, y: e.clientY, moved: false };
    });
    pages.addEventListener('pointermove', (e) => {
      if (!this.#swipe) return;
      if (Math.abs(e.clientX - this.#swipe.x) > 10 || Math.abs(e.clientY - this.#swipe.y) > 10) this.#swipe.moved = true;
    });
    pages.addEventListener('pointerup', (e) => {
      if (!this.#swipe) return;
      const dx = e.clientX - this.#swipe.x;
      const dy = e.clientY - this.#swipe.y;
      const swipe = this.#swipe;
      // Keep `moved` around for the click that follows so it doesn't also flip.
      window.setTimeout(() => {
        if (this.#swipe === swipe) this.#swipe = null;
      }, 0);
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        dx < 0 ? this.next() : this.prev();
        this.#restart();
      }
    });
    pages.addEventListener('pointercancel', () => {
      this.#swipe = null;
    });
  }

  /* ------------------------------------------------------ environment */

  #onBreakpoint = () => {
    const wasSpread = this.#mode === 'spread';
    this.#cancelFlip();
    let index = this.#index;
    this.#mode = this.#mq.matches ? 'single' : 'spread';
    if (wasSpread && this.#mode === 'single') index = index * 2;
    if (!wasSpread && this.#mode === 'spread') index = Math.floor(index / 2);
    // Rebuild dots: the count changes with the mode.
    this.querySelector('[data-dots-nav]')?.replaceChildren();
    this.#apply(index);
    this.#restart();
  };

  #onMutation = () => {
    this.#cancelFlip();
    this.#collect();
    this.querySelector('[data-dots-nav]')?.replaceChildren();
    this.#apply(this.#index);
  };

  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const page = target.closest('.scrapbook-page');
    const i = page ? (this.#mode === 'spread' ? Math.floor(this.#pages.indexOf(page) / 2) : this.#mobile.indexOf(page)) : -1;
    this.#suspended = true;
    this.#stop();
    if (i < 0) return;
    this.goTo(i, { instant: true });
  };

  #onBlockDeselect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    this.#suspended = false;
    this.#start();
  };
}

if (!customElements.get('scrapbook-book')) {
  customElements.define('scrapbook-book', ScrapbookBook);
}
